import { today } from './dates';
import { appVersion, deviceInfo } from './device';

// The floating "Góp ý" button and its chat (FeedbackButton, FeedbackChat), the same in the personal app and
// coach mode. Here: the categories, what the app sends with a message, the screenshot and where the button sits.

export const CATEGORIES = [
  { id: 'bug', label: 'Báo lỗi', hint: 'Bạn mô tả giúp mình lỗi xảy ra lúc nào nhé. Ảnh màn hình hiện tại đã được kèm sẵn, bạn có thể tắt nếu không muốn gửi.' },
  { id: 'ai_wrong', label: 'AI tính sai món', hint: 'Món nào AI tính sai, và sai ở đâu (calo, đạm, tên món)? Mở món đó rồi bấm "Báo AI tính sai" để gửi kèm luôn ảnh món.' },
  { id: 'idea', label: 'Đề xuất tính năng', hint: 'Bạn muốn Macro Mate có thêm gì? Kể cho mình nghe bạn sẽ dùng nó thế nào nhé.' },
  // From the Pro sheet's "Đăng ký Pro" on the website, until payments go through the App Store, with the message
  // written for them. The team reviews it, contacts them with how to pay, and gives Pro.
  { id: 'buy_pro', label: 'Mua Pro', hint: 'Bạn chọn gói nào cứ ghi vào tin nhắn, đội ngũ sẽ gửi cách thanh toán và kích hoạt Pro cho bạn.' },
  { id: 'other', label: 'Khác', hint: 'Bạn cứ nhắn, đội ngũ sẽ trả lời ngay tại đây.' },
];
export const categoryLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || 'Khác';

export const STATUS_LABELS = { open: 'Đang chờ', resolved: 'Đã xử lý', closed: 'Đã đóng' };

// Screen names the team reads, from the path after /me.
const SCREENS = [
  [/^\/?$/, 'Trang chủ'],
  [/^\/day/, 'Hôm nay'],
  [/^\/plan\/\d+/, 'Thực đơn – chi tiết tuần'],
  [/^\/plan/, 'Thực đơn'],
  [/^\/trends/, 'Thống kê'],
  [/^\/settings\/notifications/, 'Cài đặt nhắc nhở'],
  [/^\/settings/, 'Cài đặt'],
  [/^\/targets/, 'Mục tiêu'],
  [/^\/profile/, 'Hồ sơ'],
  [/^\/credits/, 'Gói & lượt AI'],
  [/^\/my-coach/, 'Huấn luyện viên của tôi'],
  [/^\/schedule/, 'Lịch tập với Coach'],
  [/^\/notifications/, 'Thông báo'],
  [/^\/clients\/\d+/, 'HLV · Nhật ký học viên'],
  [/^\/coach\/?$/, 'HLV · Học viên'],
  [/^\/coach\/plan/, 'HLV · Kế hoạch'],
  [/^\/coach\/requests/, 'HLV · Yêu cầu'],
  [/^\/coach\/account/, 'HLV · Tài khoản'],
  [/^\/coach\/code/, 'HLV · Mã HLV'],
  [/^\/coach\/seats/, 'HLV · Gói HLV'],
  [/^\/coach\/notifications/, 'HLV · Thông báo'],
];

export const screenName = (pathAfterBase) => SCREENS.find(([re]) => re.test(pathAfterBase))?.[1] || pathAfterBase;

// What goes with a message: app version, system, device, the screen and the date being viewed. Never the log.
export async function feedbackContext({ native, pathAfterBase, search }) {
  const date = new URLSearchParams(search).get('date') || (/^\/day/.test(pathAfterBase) ? today() : '');
  return { app_version: await appVersion(native), ...deviceInfo(), screen: screenName(pathAfterBase), path: pathAfterBase, date };
}

// The screen as the user sees it, taken when the button is tapped (before the chat opens), without the button.
// Never fails the chat: null when it takes too long or the browser can't draw it.
export async function captureScreen() {
  try {
    const { domToBlob } = await import('modern-screenshot');
    const shot = domToBlob(document.body, {
      width: window.innerWidth,
      height: window.innerHeight,
      scale: Math.min(2, window.devicePixelRatio || 1),
      type: 'image/jpeg',
      quality: 0.7,
      backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
      // Scrolled down: the page moves up by as much, so what was on screen is what's drawn.
      style: { marginTop: `-${window.scrollY}px` },
      filter: (node) => !(node instanceof Element && node.closest?.('[data-feedback-ui]')),
    });
    const blob = await Promise.race([shot, new Promise((resolve) => { setTimeout(() => resolve(null), 4000); })]);
    return blob && new File([blob], 'man-hinh.jpg', { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

// Where the button sits on this device: the edge and its distance from the bottom (null: the default spot).
const POSITION_KEY = 'macro_mate_feedback_button';

export function savedPosition() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(POSITION_KEY) || 'null');
    return saved && ['left', 'right'].includes(saved.side) ? saved : { side: 'right', bottom: null };
  } catch {
    return { side: 'right', bottom: null };
  }
}

export function savePosition(position) {
  try {
    window.localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  } catch {
    // Storage blocked: the button goes back to its default spot next time.
  }
}
